// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { directionObject, type DirectionValue, type Position } from './util';

/** A component which provides directed scrolling within a container relative to its children in response to an event. */

type ScrollBehavior = 'smooth' | 'auto' | 'instant';

type ChildKey = number | string;

export type SnapToEdge = DirectionValue<number>;

/** Out of convention, top is the number of pixels to scroll down, while left is the number of pixels to scroll right */
type ScrollBy = {| type: 'scrollBy', left: number, top: number |};

/** Scroll the child ref into the focus position */
type ScrollToChild = {| type: 'scrollToChild', key: ChildKey, focus: Position |};

/** Scrolls to the nearest child in the the specified direction. If direction is not specified, scroll to the nearest child.
 *  The scope is how "wide" it will look for the nearest child. If scope is not specified, then it is the hemisphere (90 degrees).
 *  The focus is used as the fix point and is relative to the viewport of the container
 *
 *  If direction is defined, then scalarProjection will scroll along the specified direction. Otherwise, it will scroll to bring
 *  the focus into view.
 *
 *  If minDistance is defined, then it will scroll to the nearest child greater than min distance
 *
 *  */
type ScrollToNearestChild = {|
  type: 'scrollToNearestChild',
  direction?: Position,
  scope?: number,
  focus: Position,
  scalarProjection?: boolean,
  minDistance?: number,
|};

type ScrollOptions = {|
  /** The behavior of the scroll, as defined by the spec. */
  behavior?: ScrollBehavior,
  /**  *  Scrolling sufficiently close to the edge will scroll to edge, as defined by edge snap */
  snapToEdge?: SnapToEdge,
  ...ScrollBy | ScrollToChild | ScrollToNearestChild,
|};

/** Sets the ref for an item */
export type SetChild = (key: ChildKey) => React.ElementRef<*>;
type Scroll = ScrollOptions => mixed;
type ScrollArgs = {|
  /** The scroll function. See ScrollOptions for more detail */
  scroll: Scroll,
  setContainer: React.ElementRef<*>,
  /** This requirement is not statically enforced; the children set should actually be children of the container */
  setChild: SetChild,
  wasScrolled: ChildKey => boolean,
  /** This component will occupy the ref of an element, so we want to expose the elements in case consumers need them */
  container: ?HTMLElement,
  child: { [ChildKey]: HTMLElement },
|};

type ExternalProps = {|
  children: ScrollArgs => React.Node,
|};

type Props = {|
  ...ExternalProps,
  isRtl: boolean,
|};
type State = {|
  /** The set of children "scrolled" upon from the last scroll action */
  lastScrolled: ChildKey[],
  container: ?HTMLElement,
  child: { [ChildKey]: HTMLElement },
|};
type KeyRefMap = { [ChildKey]: React.ElementRef<*> };

const makeArc = ({ top, left }: Position, scope) => {
  // Ensure radian > 0
  const angle = ((((scope % 180) + 180) % 180) * Math.PI) / 180;
  // Rotate vector up and down by the scope
  const u = {
    top: left * Math.sin(angle) + top * Math.cos(angle),
    left: left * Math.cos(angle) - top * Math.sin(angle),
  };
  const v = {
    top: left * Math.sin(-angle) + top * Math.cos(-angle),
    left: left * Math.cos(-angle) - top * Math.sin(-angle),
  };
  return { u, v };
};

const crossProduct = ({ top: lhsTop, left: lhsLeft }, { top: rhsTop, left: rhsLeft }) =>
  lhsLeft * rhsTop - lhsTop * rhsLeft;

const vectorInArc = (x, { u, v }) =>
  crossProduct(u, x) * crossProduct(u, v) >= 0 && crossProduct(v, x) * crossProduct(v, u) >= 0;

class Scrollable extends React.Component<Props, State> {
  state = { lastScrolled: [], container: null, child: {} };

  componentDidMount() {
    const { current: container } = this.containerRef;
    const mountedChildRefs = Object.entries(this.childRefs)
      // $FlowFixMe
      .map(entry => ({ key: entry[0], current: entry[1].current }))
      .filter(({ current }) => current);
    const child = mountedChildRefs.reduce((result, next) => {
      Object.assign(result, { [next.key]: next.current });
      return result;
    }, {});
    if (container) {
      this.setState({ container, child });
    } else {
      this.setState({ child });
    }
  }

  setChild = (key: ChildKey) => {
    const { childRefs } = this;
    childRefs[key] = React.createRef();
    return childRefs[key];
  };

  scroll: Scroll = (options: ScrollOptions) => {
    const { isRtl } = this.props;
    const { current: containerElement } = this.containerRef;
    const { behavior = 'auto', snapToEdge = 0 } = options;
    const snapToEdgeOptions = directionObject(snapToEdge);

    if (containerElement) {
      const {
        clientWidth,
        clientHeight,
        scrollTop,
        scrollLeft,
        scrollHeight,
        scrollWidth,
      } = containerElement;

      const differential = ({ offsetTop: childTop, offsetLeft: childLeft }, { top, left }) => {
        const relativeTop = top + scrollTop;
        // rtl places offsetTop relative to a very interesting position
        const relativeLeft = left + scrollLeft + (isRtl ? clientWidth - scrollWidth : 0);
        const dx = childLeft - relativeLeft;
        const dy = childTop - relativeTop;
        return { dx, dy };
      };

      const checkDifferentials = (dx, dy, { top, left, right, bottom }) => {
        let actualdx = dx;
        let actualdy = dy;
        if (dx > 0 && clientWidth + scrollLeft + dx + right > scrollWidth) {
          actualdx = dx + right;
        }
        if (dy > 0 && clientHeight + scrollTop + dy + bottom > scrollHeight) {
          actualdy = dy + bottom;
        }
        if (dx < 0 && scrollLeft + dx - left < 0) {
          actualdx = dx - left;
        }
        if (dy < 0 && scrollTop + dy - top < 0) {
          actualdy = dy - top;
        }
        return { dx: actualdx, dy: actualdy };
      };

      // ScrollBy option
      if (options.type === 'scrollBy') {
        const { left: dx, top: dy } = options;
        const OutOfView = ({ key, current }) => {
          const { scrollTop: elScrollTop, scrollLeft: elScrollLeft } = current;
          const newTop = elScrollTop - dy;
          const newLeft = elScrollLeft - dx;
          return {
            key,
            out: newTop <= 0 || newLeft <= 0 || newTop >= clientHeight || newLeft >= clientWidth,
          };
        };

        // last scrolled state is the set of keys which are no longer visible
        const lastScrolled = [...Object.entries(this.childRefs)]
          // $FlowFixMe : React flow types are off
          .map(entry => ({ key: entry[0], current: entry[1].current }))
          .filter(child => child.current)
          .map(OutOfView)
          .filter(view => view.out)
          .map(({ key }) => key);

        containerElement.scrollBy({ top: dy, left: dx, behavior });
        this.setState({ lastScrolled });
      }

      // ScrollToChild
      else if (options.type === 'scrollToChild') {
        const { key } = options;
        const { current: childElement } = this.childRefs[key];
        if (childElement) {
          const { dx, dy } = differential(childElement, options.focus);
          const { dx: realLeft, dy: realTop } = checkDifferentials(dx, dy, snapToEdgeOptions);
          containerElement.scrollBy({ top: realTop, left: realLeft, behavior });
          this.setState({ lastScrolled: [key] });
        }
      }
      // ScrollToNearestChild
      else if (options.type === 'scrollToNearestChild') {
        const { direction, scalarProjection = false, focus, scope = 90, minDistance = 0 } = options;
        let arc = null;
        if (direction) {
          arc = makeArc(direction, scope);
        }

        const ScopeFilter = ({ current: element }) => {
          if (!arc) {
            return true;
          }
          const { dx, dy } = differential(element, focus);
          return vectorInArc({ top: dy, left: dx }, arc);
        };

        const Distances = ({ current: element, ...child }) => {
          const { dx, dy } = differential(element, focus);
          const distance = dx * dx + dy * dy;
          let product = Infinity;
          if (direction) {
            const { top, left } = direction;
            product = top * dy + left * dx;
          }
          return { distance, product, dx, dy, ...child };
        };

        const DistancesSort = (lhs, rhs) => {
          const difference = lhs.distance - rhs.distance;
          if (difference !== 0) {
            return difference;
          }
          return lhs.product - rhs.product;
        };

        // Compute dot product from focus, and pick the shortest, positive product
        const sortedChildren = [...Object.entries(this.childRefs)]
          // $FlowFixMe : React flow types are off for ElementRef<*>
          .map(entry => ({ key: entry[0], current: entry[1].current }))
          .filter(child => child.current)
          .filter(ScopeFilter)
          .map(Distances)
          .filter(({ distance }) => distance > minDistance * minDistance)
          .sort(DistancesSort);

        if (sortedChildren[0]) {
          const { dx, dy, key, product } = sortedChildren[0];
          if (!scalarProjection) {
            const { dx: realLeft, dy: realTop } = checkDifferentials(dx, dy, snapToEdgeOptions);
            containerElement.scrollBy({ top: realTop, left: realLeft, behavior });
          } else if (direction) {
            const { top, left } = direction;
            const magnitude = top * top + left * left;
            const scalar = product / magnitude;
            const scalarLeft = scalar * left;
            const scalarTop = scalar * top;
            const { dx: realLeft, dy: realTop } = checkDifferentials(
              scalarLeft,
              scalarTop,
              snapToEdgeOptions,
            );
            containerElement.scrollBy({ top: realTop, left: realLeft, behavior });
          }
          this.setState({ lastScrolled: [key] });
        }
      }
    }
  };

  wasScrolled = (key: ChildKey) => this.state.lastScrolled.includes(`${key}`);

  containerRef: React.ElementRef<*> = React.createRef();
  childRefs: KeyRefMap = {};
  render() {
    const { children } = this.props;
    return children({
      scroll: this.scroll,
      setContainer: this.containerRef,
      container: this.state.container,
      setChild: this.setChild,
      child: this.state.child,
      wasScrolled: this.wasScrolled,
    });
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isRtl: state.i18n.isRtl,
}))(Scrollable);
